import {
  IEnrollmentsService,
  EnrollmentsPaginatedResponse,
  SoftDeleteResponse,
  EnrichedEnrollment,
} from './IEnrollmentsService';
import {
  EnrollmentService,
  InitWithAmplify,
  InitEnvironmentVariable,
} from '@PikaDoApp/npm-pikado-logic';
import type {
  NewEnrollment,
  NewCreateEnrollmentInput,
  NewUpdateEnrollmentInput,
  NewCreateFreeEnrollmentInput,
  NewCreatedEnrollment,
  NewUpdatedEnrollment,
  NewEnrollmentAfterPlacementTest,
  NewFreeEnrollment,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';
import {Amplify} from 'aws-amplify';
import {getEnvironment} from '../../lib/getEnvironment';
import {StudentsService} from '../Students/StudentsService';
import {PackagesService} from '../Package/PackagesService';
import {LevelsService} from '../Level/LevelsService';
import {TestsService} from '../Test/TestsService';
import {PlacementTestsService} from '../PlacementTest/PlacementTestsService';

export class EnrollmentsService implements IEnrollmentsService {
  enrollmentService: EnrollmentService;
  studentsService: StudentsService;
  packagesService: PackagesService;
  levelsService: LevelsService;
  testsService: TestsService;
  placementTestsService: PlacementTestsService;

  constructor() {
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.enrollmentService = new EnrollmentService();
    this.studentsService = new StudentsService();
    this.packagesService = new PackagesService();
    this.levelsService = new LevelsService();
    this.testsService = new TestsService();
    this.placementTestsService = new PlacementTestsService();
  }

  async getEnrollments(
    page: number = 1,
    limit: number = 20,
  ): Promise<EnrollmentsPaginatedResponse> {
    try {
      const response = await this.enrollmentService.newFindAllEnrollments(
        page,
        limit,
      );

      const enrollmentsData = response.findAllEnrollments;

      // Fetch student details for each enrollment
      const enrichedItems = await Promise.all(
        (enrollmentsData.data || []).map(async (enrollment: any) => {
          try {
            const student = await this.studentsService.getStudentById(
              enrollment.studentId,
            );
            return {
              ...enrollment,
              student: student,
            };
          } catch (error) {
            console.error(
              `Error fetching student ${enrollment.studentId}:`,
              error,
            );
            // Return enrollment with minimal student info if fetch fails
            return {
              ...enrollment,
              student: {
                id: enrollment.studentId,
                name: 'Unknown',
              },
            };
          }
        }),
      );

      return {
        items: enrichedItems,
        page: enrollmentsData.page,
        totalPages: enrollmentsData.totalPages,
        total: enrollmentsData.total,
        nextToken:
          enrollmentsData.page < enrollmentsData.totalPages
            ? `page-${enrollmentsData.page + 1}`
            : null,
        scannedCount: enrollmentsData.total,
      };
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      throw error;
    }
  }

  async getEnrollmentById(enrollmentId: string): Promise<EnrichedEnrollment> {
    try {
      const response =
        await this.enrollmentService.newFindEnrollmentById(enrollmentId);
      console.log('mhhr response->', response);
      const enrollment = response.findEnrollmentById;

      // Enrich enrollment with related entities
      const enrichedEnrollment: EnrichedEnrollment = {...enrollment};

      // Fetch student
      if (enrollment.studentId) {
        try {
          enrichedEnrollment.student =
            await this.studentsService.getStudentById(enrollment.studentId);
        } catch (error) {
          console.error(
            `Error fetching student ${enrollment.studentId}:`,
            error,
          );
          enrichedEnrollment.student = {
            id: enrollment.studentId,
            name: 'Unknown',
          };
        }
      }

      // Fetch package
      if (enrollment.packageId?._id) {
        try {
          const packageData = await this.packagesService.getPackageById(
            enrollment.packageId._id,
          );
          enrichedEnrollment.package = {
            id: packageData._id,
            name:
              packageData.name_i18n?.en ||
              packageData.name_i18n?.ar ||
              'Unknown',
            ...packageData,
          };
        } catch (error) {
          console.error(
            `Error fetching package ${enrollment.packageId._id}:`,
            error,
          );
          enrichedEnrollment.package = {
            id: enrollment.packageId._id,
            name: 'Unknown',
          };
        }
      }

      // Fetch level
      if (enrollment.levelId) {
        try {
          enrichedEnrollment.level = await this.levelsService.getNewLevelById(
            enrollment.levelId,
          );
        } catch (error) {
          console.error(`Error fetching level ${enrollment.levelId}:`, error);
          enrichedEnrollment.level = {
            id: enrollment.levelId,
            name: 'Unknown',
          };
        }
      }

      // Fetch placement test
      if (enrollment.placementTestId) {
        try {
          enrichedEnrollment.test =
            await this.placementTestsService.getPlacementTestById(
              enrollment.placementTestId,
            );
        } catch (error) {
          console.error(
            `Error fetching placement test ${enrollment.placementTestId}:`,
            error,
          );
          enrichedEnrollment.test = {
            id: enrollment.placementTestId,
            name: 'Unknown',
          };
        }
      }

      return enrichedEnrollment;
    } catch (error) {
      console.error('Error fetching enrollment by id:', error);
      throw error;
    }
  }

  async getEnrollmentsByStudentId(studentId: string): Promise<NewEnrollment[]> {
    try {
      const response =
        await this.enrollmentService.newFindEnrollmentsByStudentId(studentId);
      return response.findEnrollmentsByStudentId || [];
    } catch (error) {
      console.error('Error fetching enrollments by student id:', error);
      throw error;
    }
  }

  async createEnrollment(
    input: NewCreateEnrollmentInput,
  ): Promise<NewCreatedEnrollment> {
    try {
      console.log('Creating enrollment with input:', input);
      const response = await this.enrollmentService.newCreateEnrollment(input);
      return response.createEnrollment;
    } catch (error: any) {
      console.error(
        'Error creating enrollment:',
        JSON.stringify(error, null, 2),
      );

      if (error.errors) {
        throw new Error(error.errors.map((e: any) => e.message).join(', '));
      }

      throw new Error(error.message || 'Unknown error creating enrollment');
    }
  }

  async updateEnrollment(
    input: NewUpdateEnrollmentInput,
  ): Promise<NewUpdatedEnrollment> {
    try {
      console.log('Updating enrollment with input:', input);
      const response = await this.enrollmentService.newUpdateEnrollment(input);
      return response.updateEnrollment;
    } catch (error: any) {
      console.error('Error updating enrollment:', error);
      throw new Error(error.message || 'Unknown error updating enrollment');
    }
  }

  async softDeleteEnrollment(
    enrollmentId: string,
  ): Promise<SoftDeleteResponse> {
    try {
      console.log('Soft deleting enrollment:', enrollmentId);
      const response =
        await this.enrollmentService.newSoftDeleteEnrollment(enrollmentId);
      return {
        success: response.softDeleteEnrollment,
        message: response.softDeleteEnrollment
          ? 'Enrollment deleted successfully'
          : 'Failed to delete enrollment',
        enrollmentId,
      };
    } catch (error: any) {
      console.error('Error soft deleting enrollment:', error);
      throw new Error(error.message || 'Unknown error deleting enrollment');
    }
  }

  async createFreeEnrollment(
    input: NewCreateFreeEnrollmentInput,
  ): Promise<NewFreeEnrollment> {
    try {
      console.log('Creating free enrollment with input:', input);
      const response =
        await this.enrollmentService.newCreateFreeEnrollment(input);
      return response.createFreeEnrollment;
    } catch (error: any) {
      console.error('Error creating free enrollment:', error);
      throw new Error(
        error.message || 'Unknown error creating free enrollment',
      );
    }
  }

  async updateEnrollmentAfterPlacementTest(
    enrollmentId: string,
    placementTestAnswerId: string,
  ): Promise<NewEnrollmentAfterPlacementTest> {
    try {
      console.log('Updating enrollment after placement test:', {
        enrollmentId,
        placementTestAnswerId,
      });
      const response =
        await this.enrollmentService.newUpdateEnrollmentAfterPlacementTest(
          enrollmentId,
          placementTestAnswerId,
        );
      return response.updateEnrollmentAfterPlacementTest;
    } catch (error: any) {
      console.error('Error updating enrollment after placement test:', error);
      throw new Error(
        error.message ||
          'Unknown error updating enrollment after placement test',
      );
    }
  }
}
